/**
 * EnvelopeReviewPage
 *
 * Pre-send confirmation step. Owner double-checks recipients, fields, and
 * expiration before flipping the envelope to `sent`. After this point the
 * envelope is read-only — switching back to a draft would invalidate every
 * outstanding signing token, which is a deliberate path we don't ship in v1.
 */

import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, KeyRound, Send, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { HolderSwitcher, useHolderSwitcher } from '@/components/HolderSwitcher';
import { ArcheHeader } from '../../../components/ArcheHeader';
import { useEnvelope, useSendEnvelope, useRecipients } from '../hooks';
import { useSelectedHolder } from '../hooks/useSelectedHolder';
import { tierRequiresAccessCode } from '../assuranceTier';

export function EnvelopeReviewPage() {
  const { envelopeId } = useParams<{ envelopeId: string }>();
  const navigate = useNavigate();
  const { envelope, loading } = useEnvelope(envelopeId);
  const { recipients } = useRecipients(envelopeId);
  const send = useSendEnvelope(envelopeId ?? '');

  // Active workspace chip in the header. Distinct from the envelope's own
  // holder (fixed at create-time) — the chip reflects the user's preference
  // for OTHER work and persists across arches.
  const { holderId: activeHolderId, setHolderId: setActiveHolderId } = useSelectedHolder();

  // Friendly display name for the envelope's holder — used in the
  // "Sending from" callout below. The envelope holder is immutable, so we
  // look it up via the same workspace list the chip uses; if the user can
  // no longer see this holder (e.g. revoked from the org) we fall back to
  // the raw holder id.
  const switcher = useHolderSwitcher();
  const envelopeWorkspaceName =
    switcher.options.find((o) => o.holderId === envelope?.holderId)?.displayName ?? null;

  if (!envelopeId) return null;

  if (loading || !envelope) {
    return (
      <div className="flex flex-col h-full">
        <ArcheHeader archeId="arche_document_sign" title="Document Sign">
          <HolderSwitcher selectedHolderId={activeHolderId} onChange={setActiveHolderId} />
        </ArcheHeader>
        <div className="max-w-3xl mx-auto p-6 w-full space-y-3">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  const sourceFiles = envelope.files.filter((f) => f.kind === 'source');
  const fieldsByRecipient: Record<string, number> = {};
  for (const f of envelope.fields) {
    fieldsByRecipient[f.recipientId] = (fieldsByRecipient[f.recipientId] ?? 0) + 1;
  }

  async function handleSend() {
    try {
      const result = await send.mutateAsync();
      // The envelope IS sent at this point, whatever happened to the emails.
      // A failed dispatch has no retry — there is no signature-request resend
      // yet, and the raw signing link is never stored (only its hash), so
      // nothing on the envelope page can hand it over either. The only honest
      // recovery is the user knowing, by name, who may not have received a
      // link, and what they can actually do about it. Say that here, before
      // the confirmation screen reads as a clean success.
      const failed = result.deliveryFailures ?? [];
      if (failed.length > 0) {
        const names = failed.map(
          (f) => recipients.find((r) => r.recipientId === f.recipientId)?.name ?? 'a recipient',
        );
        toast({
          title: failed.length === 1 ? 'One signing email failed' : 'Some signing emails failed',
          description: `The envelope was sent, but we couldn’t email ${names.join(', ')}. A signing link can’t be resent yet — if it doesn’t arrive, void this envelope and send a new one.`,
          variant: 'destructive',
        });
      }
      // Confirmation screen — the builder URL is the wrong surface for a
      // non-draft envelope (it's the edit view) and a recipient who lands
      // on the detail view without seeing a confirmation can wonder
      // whether anything actually happened.
      navigate(`/a/arche_document_sign/envelope/${envelopeId}/sent`);
    } catch {
      // Error surfaced via send.error below
    }
  }

  /**
   * Signers still missing an access code on a Regulated envelope.
   *
   * The server enforces this inside `sendEnvelope`'s transaction and throws
   * "every signer needs an access code before it can be sent". Without a
   * check here that arrived as a red line under the Send button AFTER the
   * click, naming no one — leaving the sender to guess which of five people
   * it meant and navigate back to find out.
   *
   * `requiresAccessCode` is the server's own answer to "does this recipient
   * have a code", derived from whether a hash is stored. The code itself is
   * never returned and cannot be shown here; this list is a pointer back to
   * the builder, not a place to fix it.
   */
  const signersMissingCode = tierRequiresAccessCode(envelope.assuranceTier ?? 'standard')
    ? recipients.filter((r) => r.role === 'signer' && r.requiresAccessCode !== true)
    : [];

  // The page renders for any status — only the Send button is gated. Copy that
  // describes what sending WILL do has to know which of those it is.
  const isDraft = envelope.status === 'draft';
  // A team workspace's app grant reaches colleagues' envelopes too.
  const isSharedWorkspace = envelope.holderType !== 'personal';
  const canSend = isDraft && signersMissingCode.length === 0;

  return (
    <div className="flex flex-col h-full">
      {/* No ReturnToOriginButton here, and that is deliberate. The builder and
          the sent page both offer the return; this page is the commit step,
          and its only exit is inward ("Back to builder") on purpose. An
          outward exit sitting beside Send — irreversible, billed, and mailing
          third parties — is a mis-click at the worst possible moment, and it
          buys little: review is a transient stop one click from the builder,
          which has the return. */}
      <ArcheHeader archeId="arche_document_sign" title="Document Sign">
        <HolderSwitcher selectedHolderId={activeHolderId} onChange={setActiveHolderId} />
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/a/arche_document_sign/envelope/${envelopeId}`)}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to builder
        </Button>
      </ArcheHeader>
      <div className="max-w-3xl mx-auto p-6 w-full space-y-6">
        <div>
          <h1 className="text-xl font-semibold">Send "{envelope.title}"</h1>
          <p className="text-sm text-muted-foreground">
            Double-check the recipients and fields below. Once sent, you can void the envelope but
            cannot edit it.
          </p>
        </div>

        {/*
         * "Sending from" callout. The most important workspace check happens
         * here — Send is the irreversible action. If the user accidentally
         * built this envelope under the wrong workspace, this is their last
         * chance to notice before tokens are minted and emails go out.
         * Recovery from here is "Back to builder" → void the draft → start
         * over under the right workspace (the global chip).
         */}
        <section
          className="border-l-4 border-primary bg-primary/5 rounded-r-md p-4"
          aria-label="Workspace context"
        >
          <p className="text-sm">
            <span className="text-muted-foreground">Sending from: </span>
            <strong>{envelopeWorkspaceName ?? envelope.holderId}</strong>
            <span className="text-muted-foreground">
              {' '}
              · {recipients.length} {recipients.length === 1 ? 'recipient' : 'recipients'}
            </span>
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Recipients will see this as coming from this workspace. The workspace can't be changed
            after sending.
          </p>
        </section>

        <section className="border rounded-md p-4 space-y-2">
          <h2 className="text-sm font-semibold">Documents</h2>
          {sourceFiles.length === 0 ? (
            <p className="text-sm text-destructive">No source documents attached.</p>
          ) : (
            <ul className="text-sm list-disc pl-4">
              {sourceFiles.map((f) => (
                <li key={f.fileId}>
                  {f.fileName}
                  {f.pageCount !== null && (
                    <span className="text-muted-foreground"> ({f.pageCount} pages)</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="border rounded-md p-4 space-y-2">
          <h2 className="text-sm font-semibold">
            Recipients ({recipients.length}, {envelope.signingOrder} order)
          </h2>
          {recipients.length === 0 ? (
            <p className="text-sm text-destructive">No recipients added.</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {recipients.map((r) => (
                <li key={r.recipientId} className="flex justify-between gap-3">
                  <span>
                    {envelope.signingOrder === 'sequential' && (
                      <span className="text-xs text-muted-foreground mr-2">
                        #{r.routingOrder + 1}
                      </span>
                    )}
                    <strong>{r.name ?? r.email ?? 'Unnamed'}</strong>
                    {r.email && r.name && (
                      <span className="text-muted-foreground"> ({r.email})</span>
                    )}
                    <span className="text-xs text-muted-foreground ml-2">{r.role}</span>
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {fieldsByRecipient[r.recipientId] ?? 0} fields
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {signersMissingCode.length > 0 && (
          <section
            className="rounded-md border border-amber-500/40 bg-amber-500/10 p-4 space-y-2"
            aria-label="Access codes required"
          >
            <h2 className="text-sm font-semibold flex items-center gap-2 text-amber-900 dark:text-amber-200">
              <KeyRound className="h-4 w-4" />
              Access codes needed before sending
            </h2>
            <p className="text-sm text-amber-900 dark:text-amber-200">
              This is a Consumer / regulated envelope, so every signer needs an access code. These
              signers do not have one yet:
            </p>
            <ul className="text-sm list-disc pl-5 text-amber-900 dark:text-amber-200">
              {signersMissingCode.map((r) => (
                <li key={r.recipientId}>{r.name ?? r.email ?? 'Unnamed signer'}</li>
              ))}
            </ul>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/a/arche_document_sign/envelope/${envelopeId}`)}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Set access codes
            </Button>
          </section>
        )}

        {/*
         * Cross-arche read disclosure (design §6.2).
         *
         * Sending is the moment the originating arche gains standing read
         * access to this envelope, so this is where the user is told — before
         * the irreversible click, not in a settings page afterwards. The grant
         * is implicit in pressing Send; what makes that legitimate is that the
         * wording describes exactly what is exposed, and that it is revocable.
         *
         * The name comes from the SERVER, resolved from the stored
         * `originArcheId`. That is the whole point: `originArcheId` is
         * host-derived, not cryptographically attested, so a forged origin has
         * to become visible at the moment it would matter. Naming a real arche
         * the user does not recognise is the signal.
         *
         * Says "status, recipients and signing activity" and stops there,
         * because that is exactly the projection the read verbs return — no
         * document bytes, and no recipient email addresses. If the DTO ever
         * grows a field, this sentence grows with it or it becomes a lie.
         *
         * TWO TENSES, because this page renders for a sent envelope too — only
         * the Send button is gated on `draft`, not the route. On a sent
         * envelope the grant already exists (written inside `sendEnvelope`'s
         * transaction), so the future-tense copy would tell the user the access
         * has not happened yet, on the one screen whose whole job is an
         * accurate disclosure.
         *
         * THE REACH IS THE WORKSPACE'S, NOT THIS ENVELOPE'S. The grant is one
         * row per (workspace, app), so it covers every sent envelope the app
         * started there — on a team workspace, colleagues' included, for any
         * member who uses the app — and a Send reactivates a grant someone
         * removed. "Envelopes you send from it" understated all three; the copy
         * says them. Mirrored on the Fias AI send card (`fias-ai-tools.ts`,
         * `appAccessDisclosure`) — change both together.
         */}
        {envelope.originArcheId && (
          <section
            className="rounded-md border border-blue-500/40 bg-blue-500/10 p-4 space-y-1"
            aria-label={isDraft ? 'App access after sending' : 'App access'}
          >
            <h2 className="text-sm font-semibold flex items-center gap-2 text-blue-900 dark:text-blue-200">
              <Share2 className="h-4 w-4" />
              {envelope.originArcheName ?? 'The app that started this request'}{' '}
              {isDraft ? 'will be able to follow this' : 'can follow this'}
            </h2>
            <p className="text-sm text-blue-900 dark:text-blue-200">
              {isDraft ? 'Once you send, ' : ''}
              <strong>
                {envelope.originArcheName ?? 'the app that started this request'}
              </strong>{' '}
              {isDraft ? 'will be able to see' : 'can see'} the status, recipients and signing
              activity of every signature request it started in{' '}
              {isSharedWorkspace
                ? 'this workspace — including ones your colleagues send — for anyone here who uses it,'
                : 'your personal workspace,'}{' '}
              until {isSharedWorkspace ? 'someone removes' : 'you remove'} its access in Vault →
              Arche Access. Sending another request it started turns that access back on. It never
              sees the documents themselves, and never a draft that hasn’t been sent.
            </p>
          </section>
        )}

        <section className="border rounded-md p-4 space-y-2">
          <h2 className="text-sm font-semibold">Expiration</h2>
          <p className="text-sm">
            Each signing link expires 30 days after sending. Recipients who haven't completed by
            then will need a new link.
          </p>
        </section>

        {send.error && (
          <p className="text-sm text-destructive">
            {send.error instanceof Error ? send.error.message : 'Failed to send'}
          </p>
        )}

        <div className="flex gap-2">
          <Button onClick={handleSend} disabled={!canSend || send.isPending}>
            <Send className="h-4 w-4 mr-2" />
            {send.isPending ? 'Sending…' : 'Send for signature'}
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate(`/a/arche_document_sign/envelope/${envelopeId}`)}
            disabled={send.isPending}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
